import { appInsights } from '@/appInsights';
import Layout from '@/layouts/layout';
import { deliveryApiClient, deliveryService } from '@/lib/api';
import { selectConferenceJsonLdData } from '@/lib/conferenceStructuredData';
import { type ConferencePage } from '@/lib/conferenceStructuredData.types';
import { nextPathToUrl, urlToNextPath } from '@/lib/helpers';
import { UmbracoGrid } from '@ingeniorforeningen/figurine-cms';
import {
  IApiContentResponseModel,
  IApiMediaWithCropsResponseModel,
  PagedIApiContentResponseModel,
} from '@ingeniorforeningen/umbraco-client';

const Slug = ({
  page,
  image,
  headerNode,
  footerNode,
  conferenceJsonLdData,
}: {
  page: IApiContentResponseModel;
  image?: IApiMediaWithCropsResponseModel;
  headerNode: IApiContentResponseModel;
  footerNode: IApiContentResponseModel;
  conferenceJsonLdData?: ConferencePage[];
}) => {
  return (
    <Layout
      headerData={headerNode}
      footerData={footerNode}
      page={page}
      image={image}
      conferenceJsonLdData={conferenceJsonLdData}
    >
      <UmbracoGrid data={page} my="xl" py="xl" />
    </Layout>
  );
};

export async function getStaticPaths({
  preview,
  locales,
}: {
  preview: boolean;
  locales: string[];
}) {
  const params = (
    await Promise.all(
      locales.map(async (locale) => {
        const conf = await deliveryService
          .getContent({
            fetch: `children:${process.env.NEXT_PUBLIC_CONFERENCE_ROOT_GUID}`,
            accept_Language: locale,
            api_Key: process.env.NEXT_PUBLIC_UMBRACO_API_KEY,
            preview: preview,
            take: 1000,
          })
          .then((r) => r.toJSON() as PagedIApiContentResponseModel);

        return conf.items
          .filter((page) => page.route)
          .map((page) => ({
            locale,
            params: {
              slug: urlToNextPath(page.route!.path || '/', locale),
            },
          }));
      }),
    )
  ).flat(1);

  if (params.length < 1) {
    console.warn('No conference pages found. This should not happen');
  } else {
    console.log(
      'generating html pages for: ' + params.map((p) => '(' + p.locale + ':' + p.params.slug + ')'),
    );
  }
  return {
    paths: params,
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  preview,
  locale,
}: {
  params: { slug: string[] };
  preview: boolean;
  locale: string;
}) {
  const slug = nextPathToUrl(params.slug);
  // Validate slug before using it
  if (!slug) {
    return { notFound: true };
  }

  const { expand, accept_Language, api_Key, start_Item, footer_Item } = {
    expand:
      'properties[components[properties[' +
      'person[properties[image]],' +
      'programEntries[properties[speakers[properties[image]]]],' +
      'rows,image]]]',
    accept_Language: locale,
    api_Key: process.env.NEXT_PUBLIC_UMBRACO_API_KEY,
    start_Item: process.env.NEXT_PUBLIC_CONFERENCE_ROOT_GUID,
    footer_Item: process.env.NEXT_PUBLIC_IDA_FOOTER_GUID,
  };

  let page: IApiContentResponseModel | null = null;
  let header: IApiContentResponseModel | null = null;
  let image: IApiMediaWithCropsResponseModel | null = null;
  let footer: IApiContentResponseModel | null = null;
  let conferenceJsonLdData: ConferencePage[] = [];

  page = (await deliveryService
    .getContentByPath({
      path: slug,
      expand,
      accept_Language,
      api_Key,
      preview,
      start_Item,
    })
    .then((r) => r.toJSON() as IApiContentResponseModel)
    .catch((e) => {
      appInsights.trackException({
        error: new Error(`failed retrieving page ${slug}`),
        properties: { slug, isPreview: preview },
        severityLevel: 3,
      });

      console.log(`failed retrieving page ${slug}: `, JSON.stringify(e));
      return null;
    })) as IApiContentResponseModel | null;

  header = params.slug[0]
    ? ((await deliveryService
        .getContentByPath({ path: params.slug[0], expand, accept_Language, api_Key, preview })
        .then((r) => r.toJSON() as IApiContentResponseModel)
        .catch((e) => {
          console.log(`failed retrieving header ${slug}: `, JSON.stringify(e));
          return null;
        })) as IApiContentResponseModel | null)
    : null;

  footer = footer_Item
    ? ((await deliveryService
        .getContentById({
          id: footer_Item,
          expand,
          accept_Language,
          api_Key,
          preview,
        })
        .then((r) => r.toJSON() as IApiContentResponseModel)
        .catch((e) => {
          console.log(`failed retrieving header ${slug}: `, JSON.stringify(e));
          return null;
        })) as IApiContentResponseModel | null)
    : null;

  image = page?.properties?.image?.[0]?.id
    ? ((await deliveryApiClient
        .getMediaItemById2_0(page.properties.image[0].id, 'all', undefined, api_Key)
        .then((r) => JSON.parse(JSON.stringify(r)) as IApiMediaWithCropsResponseModel) // TODO: Remove when umbraco-client is updated to to use null and not undefined
        .catch((e) => {
          console.log(`failed retrieving image ${slug}: `, JSON.stringify(e));
          return null;
        })) as IApiMediaWithCropsResponseModel | null)
    : null;

  if (page?.contentType === 'conferences_frontpage') {
    conferenceJsonLdData = await deliveryService
      .getContent({
        fetch: `descendants:${page.id}`,
        expand,
        accept_Language,
        api_Key,
        preview,
        start_Item,
        take: 1000,
      })
      .then((response) => selectConferenceJsonLdData([page!, ...response.toJSON().items]))
      .catch((e) => {
        appInsights.trackException({
          error: new Error(`failed retrieving conference pages for ${slug}`),
          properties: { slug, isPreview: preview },
          severityLevel: 3,
        });

        console.log(`failed retrieving conference pages for ${slug}: `, JSON.stringify(e));
        return selectConferenceJsonLdData([page!]);
      });
  }

  return {
    props: {
      page: page,
      headerNode: header,
      image: image ? JSON.parse(JSON.stringify(image)) : null,
      preview: preview || null,
      footerNode: footer,
      conferenceJsonLdData,
    },
    revalidate: 60,
    notFound: !page,
  };
}

export default Slug;
